/**
 * Responsibility: assemble the rock formations a biome in eroded country builds.
 *
 * Mesa and canyon country are the same geology, and both grow arches and columns. This is the one place that says
 * how those two combine, so neither biome carries a copy of the wiring — and when a third formation arrives it is
 * added here rather than in two biome files that would then have to be kept in step.
 *
 * ## Arches are placed first, and columns are told to keep out of them
 *
 * Cross-biome clearance in `tileContent.js` deliberately never lets a biome see its own reservation, because a
 * biome is expected to guarantee its own structures do not intersect. Rock country now has two kinds that can, so
 * the guarantee has to be made here: the arch clearance test is folded into what the column placer treats as
 * reserved ground.
 *
 * The direction matters. Arches win, because an arch has exactly one thing going for it — a clean hole — and a
 * spire growing through the span destroys it, while a span passing over a spire's slot costs nothing.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createRockArches, createArchClearance } from './rockArches.js';
import { createRockColumns } from './rockColumns.js';

/**
 * Build the combined placement function for a rock biome.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId - Passed through so each biome gets its own streams.
 * @returns {(region: object, context: object) => Array<object>}
 */
export function createRockFormations(seed, biomeId) {
  const collectArches = createRockArches(seed, biomeId);
  const collectColumns = createRockColumns(seed, biomeId);
  const insideAnArch = createArchClearance(seed, biomeId);

  return function collectStructures(region, context) {
    const arches = collectArches(region, context);

    /*
      Arch clearance is evaluated from the slot rule, not from the list above, so a column near a tile edge is kept
      clear of an arch belonging to the neighbouring tile. Filtering against `arches` alone would leave columns
      growing through the half of every arch that its own tile did not build.
    */
    const columns = collectColumns(region, {
      ...context,
      isReserved: (x, z, radius) =>
        insideAnArch(x, z, radius, context.sampleWeight) ||
        (context.isReserved?.(x, z, radius) ?? false),
    });

    return [...arches, ...columns];
  };
}
